import { ref, onMounted, watch, computed } from 'vue'
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { WalletConnect } from './walletConnect';
import { ENV } from '../../../../utils/useConfig';
import getErrorMessage from '../../../../helpers/errorMessage';

const {
    block_number,
    walletStatus,
    transactionHash,
    userAction,
    walletModal,
    envParams,
    createContract,
    switchNetwork,
    initializeWallet,
    openMobileWallet,
    handleError,
} = WalletConnect();

let chatId = null;
const reqUserArgs = [];
const userHasArgs = ref(false);
const authUser = ref(null);
const athlete = ref(null);
const athleteStatus = ref(null);
const errorUserArgMessage = ref(null);
const connectedStrava = ref(false);
const isConnectedWallet = ref(false);
const blockNumber = ref(null);
const chainId = ref(null);
const currentChain = ref(null);
const expirationBuffer = ref(0);
const pledgeParam = ref(null);
const params = ref([]);
const pledgeAppId = ref(null);
const currentAppValue = ref(null);
const userArgs = ref(null);
const errorMessage = ref(null);
const messageQueue = ref([]);
const isProcessingMessage = ref(false);

export function useChat() {
    const initMessage = ref(null);
    const endMessage = ref(null);
    const historyChat = ref(false)
    const chatInput = ref(false)
    const history = ref([]);
    const ready = ref(false);
    const getResponse = ref(false);
    const showModal = computed(() => !(isConnectedWallet.value && (userHasArgs.value || connectedStrava.value)));

    onMounted(async () => {
        chatId = uuidv4();
        const props = usePage().props;
        await currentApp(pledgeAppId.value, props.apps);
        await checkShowModalSession();

        currentChain.value = ENV.current_chain;
        expirationBuffer.value = ENV.expiration_buffer;
        if (isConnectedWallet.value) {
            chainId.value = walletStatus.value[0].chainId.toString();
            blockNumber.value = block_number.value;
        } else {
            chainId.value = currentChain.value;
        }
        authUser.value = props?.auth?.user ?? null;
        if (initMessage.value) {
            getInitMessage(initMessage.value);
        }
    });

    watch(athleteStatus, (val) => {
        connectedStrava.value = val !== null;
    });

    watch(walletStatus, async (val) => {
        if (val?.length > 0) {
            isConnectedWallet.value = true;
            chainId.value = val[0]?.chainId?.toString();
            ready.value = (val[0]?.chainId?.toString() === currentChain.value);
        } else {
            isConnectedWallet.value = false;
        }
    });

    async function currentApp(action, apps) {
        const app =  apps ? apps.filter(app => app.action === action) : [];
        if (app.length > 0) {
            initMessage.value = app[0].init_message;
            endMessage.value = app[0].end_message;
        }
        return app;
    }

    function reloadData() {
        router.visit(route('main.Index'), {
            preserveState: true,
            only: [
                'error',
                'session_id',
                'chat_id',
                'authUser',
                'msg',
                'end_message',
                'pledge_param',
                'ready',
                'usage_metadata',
                'apps',
            ],
            onSuccess: (data) => {
                try {
                    const url = new URL(data.url, window.location.origin);
                    url.searchParams.delete('search');
                    const newUrl = url.origin + url.pathname;
                    window.history.replaceState({}, '', newUrl.toString());
                } catch (error) {
                    console.error('Error updating URL:', error);
                    window.history.replaceState({}, '', window.location.pathname);
                }
            },
            onError: (errors) => {
                console.error('Error reloading data:', errors);
            }
        });
    }

    const showChatInput = () => {
        chatInput.value = true;
    }

    const sendMessage = async (message) => {
        historyChat.value = true;
        const value = {
            msg: message,
            type: 'user',
            date: format(new Date(), "PPPp")
        }
        history.value.push(value);
        if (!currentAppValue.value) {
            currentAppValue.value = usePage().props.apps.filter(app => app.action === pledgeAppId.value)[0];
        }
        const request = {
            msg: message,
            appId: pledgeAppId.value,
            chatId: chatId,
            authUser: authUser.value.email,
            chainId: chainId.value,
            app: currentAppValue.value.title,
            expirationBuffer: ENV.expiration_buffer,
            oracleAddress: ENV.oracle_module,
            oracleFee: ENV.oracle_fee,
            gasLimit: ENV.gas_limit,
            script: currentAppValue.value.script,
            timezone: getTimeZone()
        }
        getRequestToGpt(request);
    }

    function getInitMessage(message) {
        chatInput.value = true;
        historyChat.value = true;
        aiResponse(message);
    }

    async function athleteValue(value = null) {
        athleteStatus.value = value?.athlete_status ?? null;
        athlete.value = value?.athlete ?? null;
        if (athlete.value || athleteStatus.value) {
            connectedStrava.value = true;
        } else {
            connectedStrava.value = false;
        }
    }

    async function showEndMessage(value) {
        historyChat.value = true;
        history.value = await value.history;
        const explorerUrl = ENV.env === 'production' ? 'https://basescan.org' : 'https://sepolia.basescan.org';
        if (transactionHash.value) {
            const endMessageWithHash = await value.end_message.replace(/<transaction_hash>/g, transactionHash.value);
            const transactionLink = `${explorerUrl}/tx/${transactionHash.value}`;
            const endMessage = endMessageWithHash.replace(/https?:\/\/sepolia\.basescan\.org\/tx\/[^ ]+/g, `[Your Transaction](${transactionLink})`);
            await aiResponse(endMessage);
        }
    }

    async function showMessage(value) {
        const values = {
            history: await value?.history ?? [],
            msg: await value?.msg ?? '',
            success: await value?.success ?? false,
        }
        historyChat.value = true;
        history.value = values.history;
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (!values.success) {
            showError(values.msg, false);
        }
        await aiResponse(values.msg);
    }

    function showError(message, showErrorMessage = true) {
      chatInput.value = false;
      errorMessage.value = getErrorMessage(message);
      if (showErrorMessage) {
        return errorMessage.value;
      } else {
        return message;
      }
    }

    function aiResponse(message = null) {
        return new Promise((resolve) => {
            if (message) {
                getResponse.value = false;
                setTimeout(async () => {
                    addAiResponse();
                    await showMessageGradually(message);
                    resolve();
                }, 700);
            } else {
                resolve();
            }
        });
    }

    function addAiResponse(value = null) {
        const response = {
            msg: '',
            type: 'ai',
            date: format(new Date(), "PPPp")
        }
        history.value.push(response ?? value);
    }

    function showMessageGradually(message) {
        return new Promise((resolve) => {
            const wordsResponse = message.split(" ");
            let index = 0;

            const interval = setInterval(() => {
                history.value[history.value.length - 1].msg += wordsResponse[index].trim() + " ";
                index++;

                if (index >= wordsResponse.length) {
                    clearInterval(interval);
                    history.value[history.value.length - 1].date = format(new Date(), "PPPp");
                    if (history.value[history.value.length - 1].code_block) {
                        history.value[history.value.length - 1].code_block.show = true;
                    }
                    chatInput.value = false;
                    resolve();
                }
            }, 100);
        });
    }

    function disconnectChat() {
        chatId = null;
        historyChat.value = false;
        chatInput.value = false;
        history.value = [];
        initMessage.value = null;
        endMessage.value = null;
        userHasArgs.value = false;
        authUser.value = null;
        athlete.value = null;
        athleteStatus.value = null;
        errorUserArgMessage.value = null;
        blockNumber.value = null;
        chainId.value = null;
        currentChain.value = null;
        expirationBuffer.value = 0;
        pledgeParam.value = null;
        params.value = [];        
        pledgeAppId.value = null;
        currentAppValue.value = null;
        userArgs.value = null;
    }

    function openStravaPopUp(username = null) {
        const posX = (screen.width / 2) - (600 / 2);
        const posY = (screen.height / 2) - (700 / 2);

        const url = import.meta.env.VITE_APP_URL + '/gpt/auth_strava?user=' + authUser.value.email + '&username=' + username;

        const popup = window.open(url, "Auth Strava", `width=${600},height=${700},left=${posX},top=${posY}`);

        const interval = setInterval(async () => {
            if (popup?.closed) {
                clearInterval(interval);
                await getOAuth();
            }
        }, 500);
    }

    function openTelegramPopUp() {
        const url = import.meta.env.VITE_APP_URL + '/gpt/auth_telegram';
        window.location.href = url;
    }

    function connectStrava(oAuthUsername = null) {
        openStravaPopUp(oAuthUsername);
    }

    function isMobile() {
        return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.matchMedia("(max-width: 768px)").matches;
    }

    async function getRequestToGpt(request) {
        getResponse.value = true;
        chatInput.value = true;
        if (showModal.value) return;
        router.post(route('main.getGptResponse'), { data: request }, {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            onSuccess: (data) => {
                const gptData = data.props;
                params.value.push(data.props);
                if (!gptData.error) {
                    if (data.props.pledge_param == null) {
                        pledgeParam.value = {
                            authUser: data.props.authUser,
                            chatId: data.props.chat_id,
                            session_id: data.props.session_id,
                            app_id: data.props.app_id,
                            expiration_buffer: data.props.expiration_buffer,
                        }
                    } else {
                        pledgeParam.value = {
                            authUser: data.props.authUser,
                            chatId: data.props.chat_id,
                            session_id: data.props.session_id,
                            app_id: data.props.pledge_param.params.app_id,
                            chain_id: data.props.chain_id,
                            pledge_goal: data.props.pledge_param.params.pledge_goal,
                            goal_unit: data.props.pledge_param.params.goal_unit,
                            pledge_currency: data.props.pledge_param.params.pledge_currency,
                            pledge_amount: data.props.pledge_param.params.pledge_amount,
                            deadline_timestamp: data.props.pledge_param.params.deadline_timestamp,
                            timezone_deadline: data.props.pledge_param.params.timezone_deadline,
                            beneficiary: data.props.pledge_param.params.beneficiary,
                            beneficiary_name: data.props.pledge_param.params.beneficiary_name,
                            expiration_buffer: data.props.expiration_buffer,
                            end_message: data.props.end_message,
                            history: history.value,
                            script: data.props.script,
                            ready: data.props.ready,
                        };
                        if (data.props.ready) {
                            ready.value = data.props.ready;
                        }
                        handleSwitchNetwork();
                        
                    }
                    aiResponse(data.props.msg);
                } else {
                    showError(data.props.validation_errors);
                    handleSwitchNetwork();
                    handleError(
                        { path: 'getRequestToGpt', domain_type: 'Chat', domain_id: gptData.chat_id },
                        `Error in Chat, Error: ${data.props.validation_errors}`,
                        { auth_user: authUser.value.email, chat_id: gptData.chat_id, error: data.props }
                    );
                }
                reloadData();
            },
            onError: (errors) => {
                console.error('Request failed:', errors);
            },
        });
    }

    async function handleSwitchNetwork() {
        if (walletStatus.value?.[0]?.chainId?.toString() !== currentChain.value) {
            switchNetwork();
        }
    }

    async function openWalletModal() {
        await walletModal.open();
        if (isMobile()) {
            await openMobileWallet();
        }
    }

    async function createPledge(value = null) {
        history.value = value.history
        await createContract(value);
    }

    async function definePledgeApp(action) {
        try {
            if (!action) {
                throw new Error('Action parameter is required');
            }

            await initializeWallet();
            pledgeAppId.value = action;
            
            return new Promise((resolve, reject) => {
                if (!router || !route) {
                    reject(new Error('Router or route not initialized'));
                    return;
                }

                router['post'](route('main.defineDapp'), { id: action }, {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: (response) => {
                        try {
                            const data = response?.props?.data;
                            if (data) {
                                data.app_id = pledgeAppId.value;
                                reqUserArgs.push(JSON.stringify(data));
                                localStorage.setItem('userArgs', reqUserArgs);
                                
                                if (!Object.keys(data)?.includes('athlete')) {
                                    verifyUserArgs(data);
                                } else {
                                    athlete.value = data.athlete ?? null;
                                    athleteStatus.value = data.status ?? null;
                                    connectedStrava.value = !!athleteStatus.value;
                                }
                            }
                            resolve(data || {});
                        } catch (error) {
                            console.error('Error in onSuccess:', error);
                            reject(new Error(error?.message || 'An error occurred processing the response'));
                        }
                    },
                    onError: (errors) => {
                        console.error('Error in API call:', errors);
                        reject(new Error(errors?.message || 'An error occurred processing the response'));
                    },
                });
            });
        } catch (error) {
            console.error('Error defining pledge app:', error);
            const missingArgs = localStorage.getItem('userArgs');
            return missingArgs ? JSON.parse(missingArgs)?.filter(arg => arg.app_id === action) : [];
        }
    }

    async function verifyUserArgs(data) {
        if (data) {
            const dataIntegrationFields = JSON.parse(data.arguments_dataset)

            userArgs.value = JSON.parse(data.user_args)
            userHasArgs.value = await checkFieldsFill(dataIntegrationFields, userArgs.value);
        }
    }

    async function checkFieldsFill(fields, userValues) {
        let result = true;
        if (userValues) {
            fields.forEach(field => {
                if (field.fixed_value === '' || field.fixed_value === null) {
                    if (!userValues.hasOwnProperty(field.name)) {
                        result = false
                    }
                }
            });
        } else {
            result = false
        }
        return result;
    }

    async function saveUserArgs(values) {
        values.pledge_app_id = pledgeAppId.value;
        router['post'](route('main.saveUserAuthArgs'), { data: values }, {
            preserveState: true,
            preserveScroll: true,
            onSuccess: (response) => {
                const data = response.props.data
                if (!data.error) {
                    userHasArgs.value = true;
                    userArgs.value = data
                    errorUserArgMessage.value = null;
                } else {
                    errorUserArgMessage.value = data.msg;
                }
            },
            onError: (errors) => {
                console.log(errors)
            },
        })
    }

    async function checkShowModalSession() {
        if (!showModal.value) {
            return true;
        } else {
            localStorage.removeItem('userArgs');
            return false;
        }
    }

    async function getOAuth()
    {
        try {
            router.post(route('main.OAuthConnection'), { email: authUser.value.email }, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: (data) => {
                    const response = data.props.data;
                    if (response) {
                        athleteStatus.value = response;
                        connectedStrava.value = !!athleteStatus.value;
                    }
                },
                onError: (errors) => {
                    console.log(errors);
                },
            });
        } catch (error) {
            console.error('Error on get oauth: ', error);
        }
    }

    function getTimeZone() {
        const offset = new Date().getTimezoneOffset(), o = Math.abs(offset);
        return (offset < 0 ? "+" : "-") + ("00" + Math.floor(o / 60)).slice(-2) + ":" + ("00" + (o % 60)).slice(-2);
    }

    function queueMessage(type, value) {
        messageQueue.value.push({ type, value });
        processMessageQueue();
    }

    async function processMessageQueue() {
        if (isProcessingMessage.value || messageQueue.value.length === 0) return;
        
        isProcessingMessage.value = true;
        const currentMessage = messageQueue.value.shift();
        
        if (currentMessage.type === 'end') {
            await showEndMessage(currentMessage.value);
        } else {
            await showMessage(currentMessage.value);
        }
        
        isProcessingMessage.value = false;
        processMessageQueue();
    }

    return {
        historyChat,
        chatInput,
        history,
        pledgeParam, 
        getResponse,  
        athlete,
        athleteStatus,
        initMessage,
        endMessage,
        isConnectedWallet,
        walletStatus,
        envParams,
        currentChain,
        connectedStrava,
        ready,
        userAction,
        showModal,
        userHasArgs,
        userArgs,
        pledgeAppId,
        errorUserArgMessage,
        errorMessage,
        switchNetwork,
        showChatInput,
        sendMessage,
        showEndMessage,
        showMessage,
        aiResponse,
        disconnectChat,
        connectStrava,
        openTelegramPopUp,
        athleteValue,
        createPledge,
        reloadData,
        definePledgeApp,
        saveUserArgs,
        getOAuth,
        openWalletModal,
        queueMessage,
    }
}
